import React, { useEffect, useState } from 'react';
import { Field, Form, Formik, FormikHelpers as FormikActions } from 'formik';
import { LinkButton, PrimaryButton } from '@magoosh/lib/button';
import {
  OrderItem,
  PaymentMethod,
  PaymentStyle,
  SubscriptionFormProps,
  SubscriptionFormValues,
  SubscriptionPlan,
  Discount,
  Cohort
} from '../types';
import {
  calculateExpirationDate,
  calculateInstallmentsDates,
  calculateInstallmentsPrice,
  months,
  years
} from '../utils';
import { formatPrice } from 'utilities/money';
import * as yup from 'yup';
import style from './style.module.scss';
import UserFormGroup from './user_form_group';
import StripePaymentFormGroup from './stripe_payment_form_group';
import InstallmentsFormGroup from './installments_form_group';
import MultiConfirmationFormGroup from './multi_confirmation_form_group';
import SingleConfirmationFormGroup from './single_confirmation_form_group';
import EuPrivacyPolicy from './eu_privacy_policy';
import UsPrivacyPolicy from './us_privacy_policy';
import { Col, Row } from 'react-bootstrap';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { bugsnagNotify } from 'utilities/bugsnag';
import {
  CardNumberElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import cx from 'classnames';
import RadioGroup from '@magoosh/pages/payment_flows/subscription_form/radio_group';
import { uniq } from 'lodash';
import moment, { tz } from 'moment-timezone';
import { formatTimeRange, longTimeZoneName } from 'utilities/time';
import { toSentence } from 'utilities/formatting';
import { FontAwesomeIcon } from '@magoosh/lib/icons';

const isPaidOrder = (discount: Discount, orderItems: OrderItem[]) => {
  if (discount) {
    return discount.discountedPrice > 0;
  } else {
    const totalPrice = orderItems.reduce((total, item) => {
      return total + item.priceCentsPerItem * item.purchasableCount;
    }, 0);
    return totalPrice > 0;
  }
};

const isPaidCardOrder = (discount: Discount, orderItems: OrderItem[], paymentMethod: PaymentMethod) => {
  if (paymentMethod !== PaymentMethod.CREDITCARD && paymentMethod !== PaymentMethod.STRIPE) {
    return false;
  }
  return isPaidOrder(discount, orderItems);
};

const shouldValidatePaymentInfo = (discount, orderItems, paymentMethod, schema) => {
  if (isPaidCardOrder(discount, orderItems, paymentMethod)) {
    return schema.required();
  } else {
    return schema.optional();
  }
};

const validationSchema = yup.object({
  authenticityToken: yup.string().required(),
  orderItems: yup.array(
    yup.object({
      purchasableCount: yup.number().label('Accounts').min(0)
    })
  ),
  email: yup.string().label('Email').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  password: yup.string().label('Password').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  passwordConfirmation: yup.string().label('Password confirmation').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  cardName: yup
    .string()
    .label('Full Name')
    .when(['discount', 'orderItems', 'paymentMethod'], shouldValidatePaymentInfo),
  cardNumber: yup.string().label('Card Number').when('paymentMethod', {
    is: PaymentMethod.CREDITCARD,
    then: yup.string().required()
  }),
  cardCvc: yup.string().label('Security Code').when('paymentMethod', {
    is: PaymentMethod.CREDITCARD,
    then: yup.string().required()
  }),
  cardZipCode: yup
    .string()
    .label('Billing Zip')
    .when(['discount', 'orderItems', 'paymentMethd'], shouldValidatePaymentInfo),
  couponCode: yup.string().label('Coupon Code')
});

const SubscriptionFormGroups: React.FC<SubscriptionFormProps> = (props) => {
  let stripe;
  let elements;

  const [buttonSubmitting, setButtonSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(
    (props.subscriptionPlanOptions || []).find((plan) => plan.preselected) ||
      props.subscriptionPlans[0] ||
      props.subscriptionPlanOptions[0]
  );
  const fallbackPlan = props.subscriptionPlanOptions && props.subscriptionPlanOptions[0];

  const [selectedCohort, setSelectedCohort] = useState((selectedPlan || fallbackPlan).cohortOptions[0]);

  const { installmentInterval, installmentIntervalCount } = selectedPlan || fallbackPlan;

  const planForPricing = selectedPlan ? selectedPlan : fallbackPlan;

  const showCohortOptions = selectedPlan.cohortOptions.length > 0;

  stripe = useStripe();
  elements = useElements();

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [clientSecret, setClientSecret] = useState<string>(null);
  const [paymentRequestMethod, setPaymentRequestMethod] = useState<string>(null);

  // Disable Stripe Payment Requests until we fix the double-charging bug
  // useEffect(() => {
  //   if (stripe) {
  //     const pr = stripe.paymentRequest({
  //       country: 'US',
  //       currency: 'usd',
  //       total: {
  //         label: planForPricing.name,
  //         amount: planForPricing.priceCents
  //       },
  //       requestPayerName: true,
  //       requestPayerEmail: false
  //     });
  //
  //     pr.canMakePayment().then((result) => {
  //       if (result) {
  //         setPaymentRequest(pr);
  //         if (result.applePay) {
  //           setPaymentRequestMethod('Apple Pay');
  //         } else if (result.googlePay) {
  //           setPaymentRequestMethod('Google Pay');
  //         }
  //       }
  //     });
  //   }
  // }, [stripe]);

  const timeZone = tz.guess();

  // For bulk orders, we are passed a collection of subscription plans to be purchased.
  // For single orders we are passed a collection of options, only one of which may be chosen.
  const initialOrderItemPlans = props.subscriptionPlans;
  if (!props.subscriptionPlans.length) {
    initialOrderItemPlans.push(selectedPlan || planForPricing);
  }

  const initialValues: SubscriptionFormValues = {
    authenticityToken: props.authenticityToken,
    orderItems: initialOrderItemPlans.map((plan) => {
      return {
        purchasable: plan,
        purchasableCount: plan.initialAmount ? plan.initialAmount : 0,
        priceCentsPerItem: plan.priceCents
      };
    }),
    signedIn: props.signedIn,
    selectedPlanId: selectedPlan?.id,
    cohortId: selectedCohort?.id,
    email: props.user ? props.user.email : '',
    password: props.user ? props.user.password : '',
    passwordConfirmation: props.user ? props.user.passwordConfirmation : '',
    paymentStyle: PaymentStyle.FULLPAYMENT,
    paymentMethod: PaymentMethod.STRIPE,
    cardName: '',
    cardNumber: '',
    cardCvc: '',
    cardExpirationDateMonth: months[0],
    cardExpirationDateYear: years[0],
    cardZipCode: '',
    couponCode: props.discount ? props.discount.couponCode : '',
    discount: props.discount,
    termsAndPolicyAccepted: props.user ? props.user.termsAndPolicyAccepted : !props.isEuUser
  };

  const formattedPrice = formatPrice(planForPricing.priceCents, planForPricing.currency);

  const formattedExpirationDate = calculateExpirationDate(
    planForPricing.durationDays,
    planForPricing.durationMonths
  );

  const formattedInstallmentsDates = calculateInstallmentsDates(
    installmentInterval,
    installmentIntervalCount
  );

  const handleOnSubmit = async (
    values: SubscriptionFormValues,
    { setSubmitting, setErrors }: FormikActions<SubscriptionFormValues>
  ): Promise<void> => {
    setButtonSubmitting(true);
    setSubmitting(true);

    if (props.beforeSubmit) {
      const shouldProceed = await props.beforeSubmit();
      if (!shouldProceed) {
        setButtonSubmitting(false);
        setSubmitting(false);
        return;
      }
    }

    let updatedValues = { ...values };

    if (!isPaidOrder(values.discount, values.orderItems)) {
      //  No modifications need to be made
      submitRequest(values, setErrors, setSubmitting);
      return;

      // Other cases will modify the form to add payment information and then submit the request
    } else if (values.paymentMethod == PaymentMethod.STRIPE) {
      if (!stripe || !elements) {
        setButtonSubmitting(false);
        setSubmitting(false);
        return;
      }

      if (props.bulkOrder) {
        // Bulk orders (the only kind of purchase that currently use the Orders system)
        // don't yet support Stripe payments that create and save a Payment Method,
        // so instead we'll create a single-use token.
        const cardNumberElement = elements.getElement(CardNumberElement);
        const response = await stripe.createToken(cardNumberElement);

        if (response.error) {
          setErrors({ stripePaymentMethod: response.error.message });
          bugsnagNotify(JSON.stringify(response.error));
          setButtonSubmitting(false);
          setSubmitting(false);
          return;
        } else {
          updatedValues = { ...values, purchaseToken: response.token.id };
        }
      } else if (!values.stripePaymentMethod) {
        const payload = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardNumberElement),
          // eslint-disable-next-line @typescript-eslint/camelcase
          billing_details: {
            name: values.cardName,
            email: values.email || null,
            address: {
              postal_code: values.cardZipCode
            }
          }
        });

        if (payload.error) {
          if (payload.error.code === 'email_invalid') {
            setErrors({ email: payload.error.message });
          } else if (payload.error.type === 'card_error' || payload.error.type === 'validation_error') {
            setErrors({ stripePaymentMethod: payload.error.message });
          } else {
            bugsnagNotify(JSON.stringify(payload.error));
          }
          setButtonSubmitting(false);
          setSubmitting(false);
          return;
        } else {
          updatedValues = { ...values, stripePaymentMethod: payload.paymentMethod };
        }
      }
    } else if (values.paymentMethod === PaymentMethod.ALIPAY) {
      await stripe
        .createSource({
          type: 'alipay',
          amount: values.discount ? values.discount.discountedPrice : selectedPlan.priceCents,
          currency: selectedPlan.currency,
          redirect: {
            return_url: `${window.location.origin}${paths.welcome('true')}&stripe=true&z_live_classes=${
              selectedPlan.hasLiveClasses
            }`
          }
        })
        .then((result) => {
          if (result.source) {
            updatedValues = {
              ...values,
              purchaseToken: result.source.id,
              alipayPaymentUrl: result.source.redirect.url
            };
          }
        });
    }
    submitRequest(updatedValues, setErrors, setSubmitting);
  };

  interface SubscriptionSubmissionResponse {
    actionRequired?: 'confirmSetup' | 'confirmPayment' | 'completePaymentRequest';
    amount?: number;
    clientSecret?: string;
    nextStepUrl?: string;
    paymentMethodId?: string;
    _redirect?: string;
  }

  const submitRequest = async (values: SubscriptionFormValues, setErrors, setSubmitting) => {
    const path = props.bulkOrder ? paths.api.orders() : paths.api.subscriptions();

    fetch(path, {
      method: 'POST',
      body: JSON.stringify(values)
    })
      .then(async (response: SubscriptionSubmissionResponse) => {
        if (response._redirect) {
          window.location.assign(response._redirect);
        } else if (response.actionRequired == 'confirmPayment') {
          stripe
            .confirmCardPayment(response.clientSecret, {
              payment_method: response.paymentMethodId,
              return_url: response.nextStepUrl,
              setup_future_usage: 'off_session'
            })
            .then((result) => {
              if (result.error) {
                setErrors({
                  _error: result.error.message
                });
                setSubmitting(false);
                setButtonSubmitting(false);
              } else {
                window.location.assign(response.nextStepUrl);
              }
            })
            .catch((error) => {
              bugsnagNotify(error);
              setFallbackError(setErrors);
              setSubmitting(false);
            });
        } else if (response.actionRequired == 'completePaymentRequest') {
          paymentRequest.update({
            total: { label: selectedPlan.name, amount: response.amount }
          });
          setClientSecret(response.clientSecret);
          paymentRequest.on('paymentmethod', async (event) => {
            const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
              response.clientSecret,
              { payment_method: event.paymentMethod.id },
              { handleActions: false }
            );
            if (confirmError) {
              setErrors({
                _error: confirmError.message
              });
              event.complete('fail');
              setSubmitting(false);
              setButtonSubmitting(false);
            } else {
              event.complete('success');
              if (paymentIntent.status === 'requires_action') {
                const { error } = await stripe.confirmCardPayment(response.clientSecret);

                if (error) {
                  setErrors({
                    _error: 'The payment failed. Please select a different payment method and try again.'
                  });
                  setSubmitting(false);
                  setClientSecret(null);
                  setButtonSubmitting(false);
                } else {
                  //  success
                  window.location.assign(response.nextStepUrl);
                }
              } else {
                // success
                window.location.assign(response.nextStepUrl);
              }
            }
          });
          paymentRequest.on('cancel', () => {
            setSubmitting(false);
            setClientSecret(null);
            setButtonSubmitting(false);
          });
        } else {
          setFallbackError(setErrors);
          setSubmitting(false);
          setClientSecret(null);
          setButtonSubmitting(false);
        }
      })
      .catch((response) => {
        if (typeof response.errors === 'string') {
          setFallbackError(setErrors);
        } else {
          if (response.errors.subscriptionPlanId) {
            setErrors({
              _error: response.errors.subscriptionPlanId
            });
          } else {
            setErrors(response.errors);
          }
        }
        setButtonSubmitting(false);
        setSubmitting(false);
      });
  };

  const setFallbackError = (setErrors) => {
    setErrors({
      _error:
        'An error has occurred. Please re-enter your payment information and try again. If you are still having trouble, please contact us at <a href="mailto:help@magoosh.com">help@magoosh.com</a>.'
    });
  };

  const disableSubmit = (values: SubscriptionFormValues) => {
    return !values.termsAndPolicyAccepted || (selectedPlan.cohortOptions.length > 0 && !values.cohortId);
  };

  const cohortOptionsConfirmationBar = () => {
    if (selectedCohort) {
      return (
        <div className={'alert alert-banner-info'}>
          Your live class time is {cohortDisplayName(selectedCohort, timeZone)}
        </div>
      );
    } else {
      return (
        <div>
          <div className={'alert alert-banner'} style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={'info-circle'} style={{ fontSize: '3rem', marginRight: '10px' }} />
            <span>Select your live cohort class time above to continue.</span>
          </div>
        </div>
      );
    }
  };

  const renderHeader = () => {
    if (props.bulkOrder) {
      return <h1>Purchase your chosen plans</h1>;
    } else if (showCohortOptions) {
      const branding = fallbackPlan.slug.toUpperCase();
      return <h1>Purchase Magoosh {branding} - Live + Premium</h1>;
    } else {
      return (
        <>
          <h1>
            {selectedPlan.priceCents > 0 ? 'Purchase' : 'Sign up for'} {selectedPlan.name}
          </h1>
          {selectedPlan.description && <div className="help-block">{selectedPlan.description}</div>}
        </>
      );
    }
  };

  return (
    <>
      {!clientSecret && renderHeader()}
      <Formik
        initialValues={initialValues}
        innerRef={props.formikRef}
        onSubmit={handleOnSubmit}
        validationSchema={validationSchema}>
        {({ values, isSubmitting, handleSubmit, setFieldValue, errors }) => {
          const finalPrice = values.discount ? values.discount.discountedPrice : planForPricing.priceCents;
          const formattedDiscountPrice = formatPrice(finalPrice, planForPricing.currency);
          const formattedInstallmentPrice = formatPrice(
            calculateInstallmentsPrice(
              values.discount ? values.discount.discountedPrice : planForPricing.priceCents
            ),
            planForPricing.currency
          );
          const requirePersonalizedPackage =
            values.orderItems.reduce((total, item) => {
              return total + item.purchasableCount;
            }, 0) >= 100;

          if (clientSecret) {
            return (
              <>
                <div className={style.walletPseudoModal}>
                  <h3>Click below to complete your payment for {selectedPlan.name}</h3>
                  You'll pay: {formattedDiscountPrice}
                  <PaymentRequestButtonElement className={style.walletButton} options={{ paymentRequest }} />
                  <LinkButton
                    onClick={() => {
                      setButtonSubmitting(false);
                      setClientSecret(null);
                    }}>
                    <small>&lt;&lt; Select a different payment method</small>
                  </LinkButton>
                </div>
              </>
            );
          }

          return (
            <Form id="subscription_payment_fields" className={style.formikForm}>
              <Field name="authenticityToken" type="hidden" />

              {!props.signedIn && !props.user && <UserFormGroup />}

              {showCohortOptions && (
                <div>
                  <legend className={style.noMargin}>
                    <Row>
                      <Col sm={9}>Select Your Live Class Cohort</Col>
                      <Col sm={3}>
                        <a style={{ fontSize: '16px' }} href="/about-live-classes#schedule" target="_blank">
                          Full Cohort Schedule Details
                        </a>
                      </Col>
                    </Row>
                  </legend>
                  <p className="u-margin-T-s">
                    You'll also get access to Magoosh Premium Self-Study Prep when you sign up for any cohort.
                  </p>
                  <p className="help-block">
                    Showing class times in your time zone: {longTimeZoneName(moment().tz(timeZone))}
                  </p>
                  <RadioGroup
                    name="cohortId"
                    optionsArray={selectedPlan.cohortOptions.map((cohort) => ({
                      value: cohort.id,
                      displayName: cohortDisplayName(cohort, timeZone)
                    }))}
                    selected={values.cohortId}
                    onChange={(newValue) => {
                      const cohort = selectedPlan.cohortOptions.find((cohort) => cohort.id == newValue);
                      setSelectedCohort(cohort);
                      setFieldValue('cohortId', cohort.id);
                    }}
                  />
                </div>
              )}

              {finalPrice > 0 && (
                <>
                  {props.allowInstallments && (
                    <InstallmentsFormGroup
                      values={values}
                      setFieldValue={setFieldValue}
                      formattedDiscountPrice={formattedDiscountPrice}
                      formattedInstallmentPrice={formattedInstallmentPrice}
                      formattedInstallmentsDates={formattedInstallmentsDates}
                    />
                  )}

                  <StripePaymentFormGroup
                    values={values}
                    setFieldValue={setFieldValue}
                    formattedDiscountPrice={formattedDiscountPrice}
                    errors={errors}
                    paymentRequestMethod={paymentRequestMethod}
                  />
                </>
              )}

              {planForPricing.priceCents > 0 && (
                <fieldset className="inputs">
                  <div className="form-group">
                    <legend>
                      <span>Confirmation</span>
                    </legend>
                    {showCohortOptions && cohortOptionsConfirmationBar()}

                    {props.bulkOrder ? (
                      <Row>
                        <Col xs={10} md={4}>
                          <MultiConfirmationFormGroup
                            values={values}
                            errors={errors}
                            requirePersonalizedPackage={requirePersonalizedPackage}
                          />
                        </Col>
                      </Row>
                    ) : (
                      <SingleConfirmationFormGroup
                        values={values}
                        formattedexpirationDate={formattedExpirationDate}
                        formattedPrice={formattedPrice}
                        formattedDiscountPrice={formattedDiscountPrice}
                        formattedInstallmentPrice={formattedInstallmentPrice}
                        showPlanName={!showCohortOptions}
                        setFieldValue={setFieldValue}
                      />
                    )}
                  </div>
                </fieldset>
              )}

              {props.isEuUser && !props.user && <EuPrivacyPolicy />}

              {errors?._error && (
                <div
                  className={cx(style.errorMessage, 'u-margin-B-m')}
                  dangerouslySetInnerHTML={{ __html: errors._error }}
                />
              )}

              <div className="form-group">
                {requirePersonalizedPackage ? (
                  <PrimaryButton
                    onClick={() => {
                      window.open('mailto:schools@magoosh.com', '_blank');
                    }}>
                    Contact Us
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={disableSubmit(values)}
                    submitting={buttonSubmitting}>
                    {values.paymentMethod == PaymentMethod.STRIPE ? 'Complete Order' : 'Continue to Payment'}
                  </PrimaryButton>
                )}
              </div>

              {finalPrice > 0 && values.paymentStyle == PaymentStyle.INSTALLMENTS ? (
                <>
                  <h5>
                    <strong>Installments Plan:</strong>
                  </h5>
                  <p className={'text-gray-light'}>
                    By clicking "Complete Order", you agree to our{' '}
                    <a href="/installment-agreement" target="_blank">
                      Installment Agreement
                    </a>{' '}
                    and authorize Magoosh to schedule a series of 4 payments from your card: 1 payment of{' '}
                    {formattedInstallmentPrice} now and 3 payments of {formattedInstallmentPrice}{' '}
                    {installmentSchedule(installmentInterval, installmentIntervalCount)}.{' '}
                    <strong>
                      There are no refunds for these installment payments outside of our 7-day money back
                      guarantee or score guarantee policies.
                    </strong>{' '}
                    An email will be sent to you with the payment schedule.
                    {!props.isEuUser && (
                      <>
                        {' '}
                        You also agree to our{' '}
                        <a href="/terms" target="_blank">
                          Terms of Use
                        </a>{' '}
                        and{' '}
                        <a href="/privacy" target="_blank">
                          Privacy Policy.
                        </a>
                      </>
                    )}
                  </p>
                </>
              ) : (
                !props.isEuUser && <UsPrivacyPolicy />
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

const cohortDisplayName = (cohort: Cohort, timeZone: string) => {
  if (cohort.onDemand) {
    return (
      <span>
        <strong>On Demand</strong>: Watch classes at your own pace and time
      </span>
    );
  }

  const startDate = moment.unix(cohort.liveClassSessions[0].startTime).tz(timeZone);
  const endDate = moment
    .unix(cohort.liveClassSessions[cohort.liveClassSessions.length - 1].startTime)
    .tz(timeZone);

  const formattedStartDate = startDate.format('MMM. DD');
  const formattedEndDate = endDate.format('MMM. DD');

  const scheduleSummary = toSentence(
    uniq(
      cohort.liveClassSessions.map((session) => {
        const startTime = moment.unix(session.startTime).tz(timeZone);
        const endTime = moment.unix(session.endTime).tz(timeZone);
        return formatTimeRange(startTime, endTime, 'ddd: ');
      })
    )
  );

  return (
    <span>
      <strong>
        {formattedStartDate} - {formattedEndDate},
      </strong>{' '}
      {scheduleSummary}
    </span>
  );
};

const installmentSchedule = (interval: string, intervalCount: number) => {
  if (intervalCount > 1) {
    return `every ${intervalCount} ${interval}s for the next ${intervalCount * 3} ${interval}s`;
  } else {
    return `every ${interval} for the next 3 ${interval}s`;
  }
};

const renderPolicy = (isEuUser: boolean, installments: boolean) => {
  if (!isEuUser && !installments) {
    return <UsPrivacyPolicy />;
  }
};

const FormWrapper: React.FC<SubscriptionFormProps> = (props) => {
  const stripePromise = loadStripe(props.stripeToken);
  return <Elements stripe={stripePromise}>{props.children}</Elements>;
};

export const SubscriptionForm: React.FC<SubscriptionFormProps> = (props) => {
  return (
    <FormWrapper {...props}>
      <SubscriptionFormGroups {...props} />
    </FormWrapper>
  );
};

export default SubscriptionForm;
